import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import {
  ACTION_COLUMNS,
  getExtraActionLabel,
  rowMatchesKeyword,
} from '#/modules/infra/settings/access/helper/permissionCatalog';

const ALL = 'all';

/**
 * 權限矩陣外框：種類切換＋搜尋＋表格（列＝功能最小單位，欄＝讀取／建立／更新／審核＋其他）。
 * 勾選方式由外層 renderCell 決定（角色用 checkbox，帳號用允許／拒絕）。
 */
const PermissionMatrix = ({
  sections,
  countGranted,
  renderCell,
  renderRowAction = null,
  rowActionLabel = '',
  legend = null,
}) => {
  const [activeKey, setActiveKey] = useState(ALL);
  const [keyword, setKeyword] = useState('');

  const visibleSections = useMemo(() => sections
    .filter((section) => activeKey === ALL || section.key === activeKey)
    .map((section) => ({
      ...section,
      rows: section.rows.filter((row) => rowMatchesKeyword(row, keyword)),
    }))
    .filter((section) => section.rows.length > 0), [sections, activeKey, keyword]);

  function sectionCount(section) {
    const perms = section.rows.flatMap((row) => row.perms);
    return `${countGranted(perms)}/${perms.length}`;
  }

  const allPerms = sections.flatMap((section) => section.rows.flatMap((row) => row.perms));
  const columnCount = ACTION_COLUMNS.length + 2 + (renderRowAction ? 1 : 0);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2.5">
        {/* 編輯器位於表單內：搜尋框按 Enter 不送出表單 */}
        <div
          className="w-full sm:w-60"
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
            }
          }}
          role="presentation"
        >
          <TableSearchInput
            useKeyword
            keyword={keyword}
            onKeywordChange={setKeyword}
            placeholder="搜尋功能，例如：出庫"
          />
        </div>
        <div role="tablist" aria-label="權限種類" className="flex flex-wrap gap-1.5">
          {[{ key: ALL, label: '全部', count: `${countGranted(allPerms)}/${allPerms.length}` },
            ...sections.map((section) => ({
              key: section.key,
              label: section.label,
              count: sectionCount(section),
            }))].map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeKey === tab.key}
              onClick={() => setActiveKey(tab.key)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm transition-colors',
                activeKey === tab.key
                  ? 'border-primary-500 bg-primary-500 text-white'
                  : 'border-gray-300 text-gray-700 hover:bg-primary-100',
              )}
            >
              {tab.label}
              <span className={cn('ml-1.5 text-xs', activeKey === tab.key ? 'text-white/80' : 'text-gray-500')}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {legend}

      {/* relative：Radix Checkbox 在 form 內會渲染 absolute 隱藏 input，需以此框為定位基準，否則外層會多出可捲動空白 */}
      <div className="relative max-h-[30rem] overflow-auto rounded-md border border-gray-300">
        <table className="w-full min-w-[40rem] border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-gray-100 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left font-medium">功能</th>
              {renderRowAction ? (
                <th className="w-16 px-2 py-2 text-center font-medium">{rowActionLabel}</th>
              ) : null}
              {ACTION_COLUMNS.map((column) => (
                <th key={column.action} className="w-16 px-2 py-2 text-center font-medium">
                  {column.label}
                </th>
              ))}
              <th className="px-3 py-2 text-left font-medium">其他</th>
            </tr>
          </thead>
          {visibleSections.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={columnCount} className="px-3 py-8 text-center text-muted-foreground">
                  找不到符合的功能
                </td>
              </tr>
            </tbody>
          ) : visibleSections.map((section) => (
            <tbody key={section.key}>
              <tr className="bg-primary-50">
                <th
                  colSpan={columnCount}
                  scope="colgroup"
                  className="border-t border-gray-300 px-3 py-1.5 text-left font-medium text-primary-800"
                >
                  {section.label}
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    已選 {sectionCount(section)}
                  </span>
                </th>
              </tr>
              {section.rows.map((row) => (
                <tr key={row.key} className="border-t border-gray-200 hover:bg-primary-100/60">
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    <span
                      className="mr-2 inline-block w-10 rounded bg-gray-100 py-0.5 text-center text-xs text-gray-500"
                    >
                      {row.moduleLabel}
                    </span>
                    <span className="text-gray-800">{row.label}</span>
                  </td>
                  {renderRowAction ? (
                    <td className="px-2 py-1.5 text-center">
                      <div className="flex justify-center">{renderRowAction(row)}</div>
                    </td>
                  ) : null}
                  {ACTION_COLUMNS.map((column) => (
                    <td key={column.action} className="px-2 py-1.5 text-center">
                      {row.actions[column.action] ? (
                        <div className="flex justify-center">
                          {renderCell(row.actions[column.action], row)}
                        </div>
                      ) : (
                        <span className="text-gray-300" aria-hidden>–</span>
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-1.5">
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                      {row.extras.map((perm) => (
                        <label key={perm.id} className="inline-flex items-center gap-1.5" title={perm.name}>
                          {renderCell(perm, row)}
                          <span className="text-gray-700">{getExtraActionLabel(perm)}</span>
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
    </div>
  );
};

PermissionMatrix.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    rows: PropTypes.array.isRequired,
  })).isRequired,
  countGranted: PropTypes.func.isRequired,
  renderCell: PropTypes.func.isRequired,
  renderRowAction: PropTypes.func,
  rowActionLabel: PropTypes.string,
  legend: PropTypes.node,
};

export default PermissionMatrix;
